import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

function RefundModal({ transaction, onClose, onRefunded }) {
  const [refundType, setRefundType] = useState('full');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const maxAmount = Number(transaction.gross_amount);
  const refundAmount = refundType === 'full' ? maxAmount : Number(amount);

  const handleRefund = async () => {
    setError('');
    if (refundType === 'partial') {
      if (!amount || isNaN(Number(amount))) { setError('Enter a valid amount.'); return; }
      if (Number(amount) > maxAmount) { setError(`Cannot exceed $${maxAmount.toFixed(2)}`); return; }
      if (Number(amount) <= 0) { setError('Amount must be greater than 0.'); return; }
    }
    setProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/transactions/${transaction.id}/refund`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: refundAmount,
          reason: reason || 'requested_by_customer'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Refund failed');
      onRefunded();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3 style={{ fontSize: 20 }}>Process Refund</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Transaction summary */}
        <div style={{
          background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px 16px',
          marginBottom: 20, fontSize: 13
        }}>
          <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{transaction.description || 'Payment'}</div>
          <div style={{ fontSize: 20, fontFamily: 'Playfair Display, serif', color: 'var(--text-primary)' }}>
            ${maxAmount.toFixed(2)} CAD
          </div>
          <div style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 12 }}>
            {new Date(transaction.created_at).toLocaleDateString('en-CA')}
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {/* Refund type */}
        <div className="form-group">
          <label className="label">Refund Type</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['full', 'partial'].map(type => (
              <button key={type} onClick={() => setRefundType(type)}
                className={refundType === type ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
                style={{ flex: 1, justifyContent: 'center', textTransform: 'capitalize' }}>
                {type === 'full' ? `Full — $${maxAmount.toFixed(2)}` : 'Partial Amount'}
              </button>
            ))}
          </div>
        </div>

        {/* Partial amount input */}
        {refundType === 'partial' && (
          <div className="form-group">
            <label className="label">Refund Amount (CAD)</label>
            <input type="number" className="input" step="0.01" min="0.01" max={maxAmount}
              placeholder={`Max $${maxAmount.toFixed(2)}`}
              value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
        )}

        {/* Reason */}
        <div className="form-group">
          <label className="label">Reason (optional)</label>
          <select className="input" value={reason} onChange={e => setReason(e.target.value)}>
            <option value="requested_by_customer">Requested by customer</option>
            <option value="duplicate">Duplicate charge</option>
            <option value="fraudulent">Fraudulent</option>
            <option value="">Other</option>
          </select>
        </div>

        {/* Refund summary */}
        <div style={{
          background: 'rgba(224,85,85,0.08)', border: '1px solid rgba(224,85,85,0.2)',
          borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13
        }}>
          <div style={{ color: 'var(--text-secondary)' }}>
            Refunding <strong style={{ color: 'var(--danger)' }}>
              ${(refundType === 'full' ? maxAmount : Number(amount) || 0).toFixed(2)} CAD
            </strong> to the client's original payment method.
            This cannot be undone.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-danger" onClick={handleRefund} disabled={processing}
            style={{ flex: 1, justifyContent: 'center' }}>
            {processing ? 'Processing...' : 'Confirm Refund'}
          </button>
          <button className="btn btn-secondary" onClick={onClose}
            style={{ flex: 1, justifyContent: 'center' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Transactions() {
  const { profile, isAdmin, isSuperAdmin } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundModal, setRefundModal] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (profile?.id) fetchTransactions();
  }, [profile]);

  const fetchTransactions = async () => {
    setLoading(true);
    let query = supabase
      .from('transactions')
      .select('*, coach:coach_id(full_name, email)')
      .order('created_at', { ascending: false });

    // Coaches only see their own, admins see all
    if (!isAdmin) {
      query = query.eq('coach_id', profile.id);
    }

    const { data } = await query;
    setTransactions(data || []);
    setLoading(false);
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

  const filtered = transactions.filter(t => {
    if (filter === 'refunded') return t.status === 'refunded';
    if (filter === 'completed') return t.status === 'completed';
    if (search) {
      return t.description?.toLowerCase().includes(search.toLowerCase()) ||
        t.coach?.full_name?.toLowerCase().includes(search.toLowerCase());
    }
    return true;
  });

  const totalGross = filtered.reduce((s, t) => s + Number(t.gross_amount || 0), 0);
  const totalHma = filtered.reduce((s, t) => s + Number(t.hma_amount || 0), 0);
  const totalCoach = filtered.reduce((s, t) => s + Number(t.coach_amount || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, marginBottom: 8 }}>Transactions</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {isAdmin ? 'All payments across all coaches' : 'Your payment history'}
        </p>
      </div>

      {/* Summary stats */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-label">◇ Total Revenue</div>
          <div className="stat-value">{fmt(totalGross)}</div>
          <div className="stat-sub">gross collected</div>
        </div>
        {isAdmin && (
          <div className="stat-card">
            <div className="stat-label">◆ HMA Revenue</div>
            <div className="stat-value">{fmt(totalHma)}</div>
            <div className="stat-sub">after splits</div>
          </div>
        )}
        <div className="stat-card">
          <div className="stat-label">✦ {isAdmin ? 'Coach Payouts' : 'My Revenue'}</div>
          <div className="stat-value">{fmt(totalCoach)}</div>
          <div className="stat-sub">paid to coaches</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">◉ Transactions</div>
          <div className="stat-value">{filtered.length}</div>
          <div className="stat-sub">total payments</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all', 'completed', 'refunded'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={filter === f ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ textTransform: 'capitalize' }}>
            {f}
          </button>
        ))}
        <input className="input" placeholder="Search..." value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ maxWidth: 200, marginLeft: 'auto' }} />
      </div>

      {/* Transactions table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, color: 'var(--text-secondary)' }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-muted)' }}>
            No transactions found.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Description</th>
                  {isAdmin && <th>Coach</th>}
                  <th>Gross</th>
                  <th>HMA Cut</th>
                  <th>Coach Keeps</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{t.description || 'Payment'}</div>
                      {t.stripe_payment_intent_id && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                          {t.stripe_payment_intent_id.slice(0, 20)}...
                        </div>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {t.coach?.full_name || '—'}
                      </td>
                    )}
                    <td style={{ fontWeight: 500 }}>{fmt(t.gross_amount)}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{fmt(t.hma_amount)}</td>
                    <td style={{ color: 'var(--success)', fontWeight: 500 }}>{fmt(t.coach_amount)}</td>
                    <td>
                      <span className={`badge ${
                        t.status === 'completed' ? 'badge-active' :
                        t.status === 'refunded' ? 'badge-inactive' :
                        'badge-pending'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                      {new Date(t.created_at).toLocaleDateString('en-CA')}
                    </td>
                    <td>
                      {t.status === 'completed' && t.stripe_payment_intent_id && (
                        <button className="btn btn-danger btn-sm"
                          onClick={() => setRefundModal(t)}>
                          Refund
                        </button>
                      )}
                      {t.status === 'refunded' && (
                        <span style={{ fontSize: 12, color: 'var(--danger)' }}>Refunded</span>
                      )}
                      {t.status === 'completed' && !t.stripe_payment_intent_id && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {refundModal && (
        <RefundModal
          transaction={refundModal}
          onClose={() => setRefundModal(null)}
          onRefunded={fetchTransactions}
        />
      )}
    </div>
  );
}
